import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/landing/Reveal";

const CHAPTERS = [
  {
    id: "01",
    title: "Dokumentation",
    text: "Jeder Auftrag lückenlos festgehalten: Fotos, Messdaten und Verlauf werden strukturiert dokumentiert — revisionssicher und sofort verfügbar.",
    items: ["Protokoll in Echtzeit", "GPS & Zeitstempel", "Fotodokumentation vor Ort", "Offline im Funkloch"],
    img: "/assets/building-damage.jpg",
    alt: "Drohnenaufnahme eines maroden Gebäudes zur Zustandsdokumentation",
  },
  {
    id: "02",
    title: "Einsatzunterstützung",
    text: "Wenn es schnell gehen muss: Lagebilder und Live-Daten aus der Luft — direkt an Einsatzleitung und Fachberater, bevor Bodenkräfte vor Ort sind.",
    items: ["Lagebild in Minuten", "Live-Übertragung möglich", "Großflächige Aufklärung", "Übergabe an die Einsatzleitung"],
    img: "/assets/aerial-observatory.jpg",
    alt: "Luftbild-Lageaufnahme einer weitläufigen Anlage",
  },
  {
    id: "03",
    title: "Drohnenservice",
    text: "Inspektion ohne Gerüst und ohne Stillstand: Windkraftanlagen, Schornsteine, Funkmasten und Dächer — wir fliegen nah ran und liefern verwertbare Daten.",
    items: ["Anlagen-Inspektion", "Dach- & Schornsteinprüfung", "Orthofotos & Bestandsdaten", "Zugelassen & versichert"],
    img: "/assets/wind-turbine.jpg",
    alt: "Nahaufnahme einer Windkraftanlage bei der Drohneninspektion",
  },
  {
    id: "04",
    title: "Kundenportal",
    text: "Aufträge ohne Umwege: Anfragen direkt über das Portal stellen — Status, Termine und Ergebnisse bleiben für alle Seiten transparent.",
    items: ["Anfragen direkt im System", "Status transparent", "Ergebnisse digital geliefert", "Dokumentiert mit FIELD-REPORT-11"],
    img: "/assets/chimney-inspection.jpg",
    alt: "Blick von oben in einen Industrieschornstein — geliefertes Inspektionsergebnis",
  },
];

function SpotlightImage({ src, alt, id }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.18, 1.02]);
  const y = useTransform(scrollYProgress, [0, 1], ["-5%", "5%"]);

  return (
    <div ref={ref} className="relative aspect-[16/11] overflow-hidden border border-white/15">
      <motion.img
        data-testid={`chapter-image-${id}`}
        src={src}
        alt={alt}
        loading="lazy"
        style={{ scale, y }}
        className="h-full w-full object-cover brightness-[0.8] contrast-125 grayscale-[55%]"
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, transparent 25%, rgba(9,9,11,0.82) 100%)",
        }}
      />
      <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-[#F5B301]" />
      <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-[#F5B301]" />
      <span className="absolute bottom-3 left-3 font-tac text-[9px] tracking-[0.3em] text-white/50">
        DROPS TFR // AUFNAHME {id}
      </span>
    </div>
  );
}

export default function Chapters() {
  return (
    <section id="funktionen" data-testid="chapters-section" className="relative">
      <div className="border-b border-white/10 px-6 py-20 lg:px-16 lg:py-28">
        <Reveal>
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">LEISTUNGEN</p>
          <h2 className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-7xl">
            Vier Leistungen.
            <br />
            <span className="text-stroke">Ein Anspruch.</span>
          </h2>
        </Reveal>
      </div>

      {CHAPTERS.map((chapter, i) => (
        <article
          key={chapter.id}
          data-testid={`chapter-${chapter.id}`}
          className="relative grid gap-10 overflow-hidden border-b border-white/10 px-6 py-20 lg:grid-cols-12 lg:gap-0 lg:px-16 lg:py-32"
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute top-8 select-none font-display text-[9rem] font-black leading-none text-stroke opacity-40 lg:text-[16rem] ${
              i % 2 ? "left-4 lg:left-16" : "right-4 lg:right-16"
            }`}
          >
            {chapter.id}
          </span>

          <div className={`relative z-10 lg:col-span-6 ${i % 2 ? "lg:order-2 lg:pl-20" : "lg:pr-20"}`}>
            <Reveal>
              <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">
                KAPITEL {chapter.id}
              </p>
              <h3 className="mt-5 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-6xl">
                {chapter.title}
              </h3>
              <p className="mt-7 max-w-md font-tac text-sm leading-relaxed text-[#A1A1AA]">
                {chapter.text}
              </p>
              <ul className="mt-9 space-y-3.5 border-t border-white/10 pt-7">
                {chapter.items.map((item) => (
                  <li
                    key={item}
                    className="group flex items-center gap-3 font-tac text-xs uppercase tracking-[0.15em] text-white/80 transition-colors duration-300 hover:text-white"
                  >
                    <span className="h-1.5 w-1.5 bg-[#F5B301] transition-transform duration-300 group-hover:rotate-45 group-hover:scale-125" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className={`relative z-10 lg:col-span-6 ${i % 2 ? "lg:order-1 lg:pr-20" : "lg:pl-20"}`}>
            <Reveal delay={0.15}>
              <SpotlightImage src={chapter.img} alt={chapter.alt} id={chapter.id} />
            </Reveal>
          </div>
        </article>
      ))}
    </section>
  );
}
